import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '@/context/AuthContext';
import React, { useEffect, useState } from 'react';
import { useWorkspace } from "@/context/WorkspaceContext";
import { Modal, VbButton, VbError } from "@/components/vb";
import { loadOnboardingStatus, type OnboardingStatus } from "@/services/onboardingService";
import { WorkspaceRole } from "@/types/workspace";
import CancellationGate from "@/components/cancellation/CancellationGate";

const ONBOARDING_NOTICE = "You need to onboard yourself first.";

const ClosedWorkspaceGate: React.FC = () => {
  const navigate = useNavigate();
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadOnboardingStatus()
      .then((status) => {
        if (!cancelled) setOnboardingStatus(status);
      })
      .catch(() => {
        if (!cancelled) setOnboardingStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canStartTrial = onboardingStatus?.onboarding_deferred === true;

  return (
    <div className="vb-root min-h-screen bg-vb-bg-subtle">
      <Modal
        open
        onOpenChange={() => undefined}
        title="This workspace has closed"
        description="The owner's subscription ran out. Your own workspace takes minutes to start."
        showCloseButton={false}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        footer={
          canStartTrial ? (
            <VbButton
              size="lg"
              onClick={() =>
                navigate("/onboarding", {
                  replace: true,
                  state: { onboardingNotice: ONBOARDING_NOTICE },
                })
              }
            >
              Start My 10-Day Trial
            </VbButton>
          ) : undefined
        }
      />
    </div>
  );
};

export const PrivateRoute = () => {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const {
    loading: workspaceLoading,
    error: workspaceError,
    activeWorkspace,
    endedWorkspace,
    workspaceFallbackPending,
    clearWorkspaceFallback,
    retryWorkspaceLoad,
  } = useWorkspace();

  useEffect(() => {
    if (workspaceFallbackPending && location.pathname === "/") {
      clearWorkspaceFallback();
    }
  }, [clearWorkspaceFallback, location.pathname, workspaceFallbackPending]);

  if (loading || (isAuthenticated && workspaceLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (workspaceError) {
    return (
      <div className="vb-root min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-xl">
          <VbError
            title="We couldn't load your workspace"
            description={workspaceError}
            onRetry={() => void retryWorkspaceLoad()}
          />
        </div>
      </div>
    );
  }

  if (
    !activeWorkspace &&
    endedWorkspace &&
    endedWorkspace.role !== WorkspaceRole.Owner
  ) {
    return <ClosedWorkspaceGate />;
  }

  if (
    !activeWorkspace &&
    endedWorkspace?.role === WorkspaceRole.Owner
  ) {
    return <CancellationGate />;
  }

  if (workspaceFallbackPending && location.pathname !== "/") {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};
